import type { CompanionBondPayload } from '../api/contracts.js';

/**
 * Metadata required when referencing a shared Move object inside a Sui transaction.
 * We cache these values after the first read and pass them to `tx.sharedObjectRef(...)`.
 */
export interface SharedObjectDetails {
  /** Version the object first became shared; Sui requires this exact value when building transactions. */
  initialSharedVersion: string;
  /** Whether the object is expected to be mutable when the transaction executes. */
  mutable: boolean;
}

/**
 * Lightweight entry returned by registry scans. Used to schedule and fan out follow-up queries.
 */
export interface PetRegistryEntry {
  /** ID of the on-chain status object backing the virtual pet. */
  statusId: string;
  /** ID of the on-chain pet object itself. */
  petId: string;
}

/**
 * Snapshot produced by background jobs to determine which pets need attention.
 * Avoids pulling the full status payload unless a follow-up action is required.
 */
export interface PetStatusSummary {
  /** Status object ID used for follow-up reads and ticks. */
  statusId: string;
  /** Pet object ID the status belongs to. */
  petId: string;
  /** Schema version on chain; allows rolling migrations and backwards compatibility checks. */
  schemaVersion: number;
  /** Last successful maintenance tick in milliseconds since epoch. */
  lastTickMs: number;
  /** Remaining sponsored gas balance stored on chain as a bigint. */
  gasBalanceMist: bigint;
  /** Flag used to exclude hibernating pets from real-time tick scheduling. */
  hibernated: boolean;
  /** Ownership info for the status object itself; null when it is not a shared object. */
  sharedDetails: SharedObjectDetails | null;
}

/**
 * Result emitted after attempting to tick a pet. The backend queues these objects for logging
 * and user notifications.
 */
export interface TickOutcome {
  /** Indicates whether the Move call succeeded. */
  success: boolean;
  /** Status object that was targeted by the tick. */
  statusId: string;
  /** Optional pet ID (may be absent when the tick fails before loading the pet). */
  petId?: string;
  /** Digest of the executed transaction when available. */
  digest?: string;
  /** Gas value we claimed to use when submitting the transaction. */
  reportedGasUsedMist?: string;
  /** Actual gas charged by validators if execution succeeded. */
  actualGasUsedMist?: string;
  /** Updated gas balance returned by a post-tick status fetch. */
  newGasBalanceMist?: string;
  /** Indicates the tick was intentionally skipped (e.g. throttled). */
  skipped?: boolean;
  /** Extra context highlighting non-fatal issues, such as gas mismatches. */
  warning?: string;
  /** Failure reason when the tick request did not succeed. */
  error?: string;
}

/**
 * Data returned to the API caller after minting a new pet so the UI can refresh.
 */
export interface MintPetResult {
  /** Newly created pet object ID (Sui blockchain object ID). */
  petId: string;
  /** Transaction digest for explorers or later dispute resolution. */
  txDigest: string;
  /** On-chain mint number (scarcity rank) parsed from the SuikamonMintedEvent. */
  mintNumber?: number;
}

/**
 * Result emitted after attempting to execute a pet action (feed, train, clean, etc).
 * Used by both frontend and backend to communicate action results.
 */
export interface ActionOutcome {
  /** Indicates whether the Move call succeeded. */
  success: boolean;
  /** Status object that was targeted by the action. */
  statusId?: string;
  /** Pet ID associated with the action. */
  petId?: string;
  /** Digest of the executed transaction when available. */
  digest?: string;
  /** Gas value we claimed to use when submitting the transaction. */
  reportedGasUsedMist?: string;
  /** Actual gas charged by validators if execution succeeded. */
  actualGasUsedMist?: string;
  /** Updated gas balance returned by a post-action status fetch. */
  newGasBalanceMist?: string;
  /** Indicates the action was intentionally skipped (e.g. throttled, insufficient gas). */
  skipped?: boolean;
  /** Extra context highlighting non-fatal issues, such as gas mismatches. */
  warning?: string;
  /** Failure reason when the action request did not succeed. */
  error?: string;
  /** Timestamp when the action was applied (milliseconds since epoch). */
  appliedAtMs?: number;
  /**
   * Care-minigame amplification the backend granted for this action (present
   * only when the request carried a minigame result — see minigameConstants).
   */
  minigame?: import('./minigameConstants.js').MinigameGrant;
}

/**
 * Pet NFT struct - maps 1:1 to Move's Pet struct (owned object).
 * Represents the tradeable NFT with immutable metadata.
 */
export interface Pet {
  /** Pet object ID. */
  id: string;
  /** Display name chosen by the player. */
  name: string;
  /** Lore or flavour text presented in the UI. */
  description: string;
  /** URL pointing to the pet's image asset. */
  image_url: string;
  /** Rarity tier that affects mint odds and rewards. */
  rarity: number;
  /** Status object bound to this pet. */
  status_id: string;
  /** Species ID of the pet (from on-chain Pet struct). */
  species: number;
  /** Evolution stage (from on-chain Pet struct). */
  stage: number;
  /** Current level (from on-chain Pet struct). */
  level: number;
  /** Timestamp when the pet was born in milliseconds (from on-chain Pet struct). */
  birth_time: number;
  /** Mint number (sequential number for this species, from on-chain Pet struct). */
  mint_number: number;
}

/**
 * PetStatus struct - maps 1:1 to Move's PetStatus struct (shared object).
 * Represents the mutable gameplay state that changes with interactions and time.
 */
export interface PetStatus {
  // === Object Identity & Ownership ===
  /** Status object ID (Move object address). */
  id: string;
  /** Pet object ID this status belongs to. */
  pet_id: string;
  /** Owner address of the pet. */
  owner: string;
  /** Schema version to coordinate server/client expectations. */
  schema_version: number;
  /** Species ID of the pet. */
  species: number;

  // === Lifecycle & State Flags ===
  /** True when the pet is placed in hibernation and should not degrade. */
  hibernated: boolean;
  /** True when the pet is still in egg form (stage 0). */
  is_egg: boolean;
  /** True when the pet is sick and needs medicine. */
  sick: boolean;

  // === Core Stats & Progression ===
  /** Evolution stage (0 = egg, 1 = baby, 2+ = evolved forms). */
  stage: number;
  /** Current level (increases with XP). */
  level: number;
  /** Experience points accrued towards the next level. */
  xp: number;

  // === Needs & Care Meters ===
  /** Hunger meter (0 = full, larger numbers = hungry). */
  hunger: number;
  /** Happiness meter describing current mood. */
  happiness: number;
  /** Energy meter powering activities and sleep decisions. */
  energy: number;
  /** Health level (0-100 scale): 100 = healthy, <50 = sick */
  health: number;
  /** Cleanliness level (0-100 scale): 100 = clean, 0 = very dirty */
  cleanliness: number;

  // === Behavioral & Condition State ===
  /** Discipline meter used to adjust behaviour and event probabilities. */
  discipline: number;
  /** Overall condition state (1=Sick, 2=Hungry, 3=Dirty, 4=Tired, 5=Happy, 0=special state) */
  overall_condition: number;

  // === Status Indicators ===
  /** Whether the pet is currently in rest mode */
  resting: boolean;
  /** Suika resource balance (consumed per tick, triggers hibernation when depleted) */
  suika_balance: number;
  /**
   * Custody lifecycle of the pet's NFT (Phase 1). 'in_custody' = backend custody wallet
   * holds it, play is frictionless (no signatures); 'pending_deposit' = the user still
   * holds the NFT and must deposit it to play gas-free; 'withdrawn' = pulled out to the
   * user's wallet to trade. Optional: undefined for payloads predating the custody model.
   */
  custody_status?: 'pending_deposit' | 'in_custody' | 'reserve' | 'withdrawn';
  /**
   * True when the NFT is currently escrowed in a marketplace kiosk/shared object (e.g.
   * listed for sale on Tradeport) rather than held in the player's wallet. Such a pet
   * can't be played until it's delisted and moved to play. Transient on-chain condition,
   * not persisted.
   */
  marketplace_listed?: boolean;

  // === Timing & Scheduling ===
  /** Last user action timestamp for spam protection (3s cooldown). */
  last_action_ms: number;
  /** Last time energy was manually refilled (24h cooldown). */
  last_refill_ms: number;

  // === Rest Mode Management ===
  /** Timestamp when rest mode was started */
  rest_start_ms: number;
  /** Energy level when rest mode started (buffer) */
  rest_energy_buffer: number;

  // === Care Streaks & Abandonment ===
  /** Consecutive days since birth (breaks to 0 if abandoned for 24+ hours) */
  care_streak_days: number;
  /** Bonus XP applied to eligible care actions (1 XP per streak day, max 10 XP) */
  care_streak_xp_bonus: number;
  /** Whether pet is abandoned (all stats = 0) */
  is_abandoned: boolean;
  /** Duration pet has been abandoned in milliseconds (0 if not abandoned) */
  abandoned_duration_ms: number;
  /**
   * Real time since the pet's last CARE action, in milliseconds (0 if never / unknown).
   * Drives companion welcome-back greetings. Distinct from `last_action_ms`, which tracks
   * the last DB write of any kind (including background scheduler ticks). Optional so
   * payloads predating the field stay valid.
   */
  time_since_last_action_ms?: number;

  // === Gas & Economy ===
  /** Timestamp when the pet was born (created) in milliseconds since epoch. */
  birth_ms: number;
  /** Timestamp when current hibernation started (0 if not hibernated). */
  hibernation_start_ms: number;
  /** Total time spent hibernated across all hibernation periods (milliseconds). */
  total_hibernated_ms: number;

  // === Breeding State ===
  /** Whether pet can breed at its current level (milestone levels: 10, 20, 30...) */
  can_breed_at_current_level: boolean;

  // === PVP & Competitive ===
  /** ELO rating for ranked PVP battles (default: 1200, range: 0-5000) */
  elo_rating?: number;

  // === Companion Bonding ===
  /**
   * Companion-bonding payload (roster standing, facts, next prompt, latest level memory).
   * Only computed for owner-facing, account-keyed reads where the acting `accountId` is
   * known (e.g. `listPetsByAccount`) — omitted/`null` elsewhere (bulk/public/no-account
   * reads) to avoid extra per-pet DB round-trips. `null` also on a best-effort failure.
   */
  companionBond?: CompanionBondPayload | null;
}

/**
 * Helper type combining Pet NFT with its PetStatus.
 * Used in API responses when both objects are needed together.
 */
export type PetWithStatus = {
  pet: Pet;
  status: PetStatus;
};

// ============================================================================
// Species Configuration Types
// ============================================================================

/**
 * Element types for species categorization.
 * Each element has visual theming and future gameplay implications.
 */
export type ElementId = "Nature" | "Water" | "Thunder" | "Fire" | "ComingSoon";

/**
 * Rarity tiers affecting mint caps, pricing, and visual styling.
 */
export enum Rarity {
  Common = "Common",
  Rare = "Rare",
  Epic = "Epic",
  Legendary = "Legendary",
  Unique = "Unique",
}

/**
 * Species-specific behavior configuration.
 * Defines XP progression and evolution stages.
 */
export type SpeciesBehavior = {
  /** Minimum XP gained from actions when stats are poor */
  baseXpMin: number;
  /** Maximum XP gained from actions when stats are perfect */
  baseXpMax: number;
  /** Maximum evolution stage this species can reach */
  maxStage: number;
  /** Level thresholds per stage transition (array length = maxStage, stageLevels[N] = level required to evolve FROM stage N to stage N+1) */
  stageLevels: number[];
};

// ============================================================================
// PVP Combat Types
// ============================================================================

/**
 * Canonical combat special tags.
 *
 * `None` is a real value on purpose: "this skill has no special effect" is game
 * data, not an omitted field. Keep this object as the single source of truth
 * for combat mechanics used by configs, UI, simulations, and the battle engine.
 */
export const COMBAT_SPECIAL_EFFECT = {
  None: "none",

  // Damage / sustain effects
  Lifesteal: "lifesteal",
  Dot: "dot",
  DrainLife: "drainlife",
  Execute: "execute",
  Wound: "wound",
  Heal: "heal",

  // Control effects
  Stun: "stun",
  ForceMagic: "force_magic",
  ForceRecover: "force_recover",
  BlockMagic: "block_magic",
  BlockRecover: "block_recover",

  // Defensive effects
  Shield: "shield",

  // Combo effects — read a condition already on the board instead of applying one.
  // These are the PUNISH half of the kit: a Battle Card (or a previous turn) puts the
  // condition down, and the species' magic signature is what converts it. See
  // `CombatPunish` and docs/superpowers/specs/2026-08-01-magic-signature-review-vs-effect-deck.md.
  ExploitDot: "exploit_dot",
  ExploitWound: "exploit_wound",
  ExploitStun: "exploit_stun",
  ExploitShield: "exploit_shield",
  PunishAttack: "punish_attack",

  // Card-only effects — printed on a Battle Card, never on a species kit. Each is ONE condition
  // a player can already see on the board and ONE number (docs/BATTLE_CARDS_TUNING_LOG.md,
  // the archetype-synergy row): the card text must read in a single short sentence.
  ForceAttack: "force_attack",
  StrikeStunned: "strike_stunned",
  StrikeShielded: "strike_shielded",
  /** RETIRED (replaced by Reaper, `damage_vs_hurt`) — kept only so stored `recover-heal_vs_hurt*` ids still alias. */
  HealVsHurt: "heal_vs_hurt",
  DamageVsHurt: "damage_vs_hurt",
  HealVsLow: "heal_vs_low",
  /** RETIRED (replaced by Last Stand) — kept only so stored `recover-heal_vs_wounded*` ids still alias. */
  HealVsWounded: "heal_vs_wounded",
} as const;

export type CombatSpecialEffect =
  typeof COMBAT_SPECIAL_EFFECT[keyof typeof COMBAT_SPECIAL_EFFECT];

export type ActiveCombatSpecialEffect = Exclude<
  CombatSpecialEffect,
  typeof COMBAT_SPECIAL_EFFECT.None
>;

export function hasCombatSpecial(special: CombatSpecialEffect): special is ActiveCombatSpecialEffect {
  return special !== COMBAT_SPECIAL_EFFECT.None;
}

/**
 * Strategy archetype (cluster) a combat species belongs to.
 *
 * A species' archetype identity is defined by its magic special — see
 * combatArchetypes.ts for the defining-special map and the consistency
 * validation that enforces it (e.g. a Shield species must use the shield
 * magic special). Used by the archetype-level meta simulation tool.
 */
export const COMBAT_ARCHETYPE = {
  DoT: "DoT",
  Shield: "Shield",
  Stun: "Stun",
  Wound: "Wound",
  Execute: "Execute",
} as const;

export type CombatArchetype =
  typeof COMBAT_ARCHETYPE[keyof typeof COMBAT_ARCHETYPE];

/**
 * The effects that READ a condition rather than applying one — the punish half of a kit.
 *
 * Deliberately a subset of {@link COMBAT_SPECIAL_EFFECT} rather than a parallel union, so a
 * punish can never name an effect the engine does not resolve.
 */
export type CombatPunishEffect =
  | typeof COMBAT_SPECIAL_EFFECT.ExploitDot
  | typeof COMBAT_SPECIAL_EFFECT.ExploitStun
  | typeof COMBAT_SPECIAL_EFFECT.ExploitWound
  | typeof COMBAT_SPECIAL_EFFECT.ExploitShield
  | typeof COMBAT_SPECIAL_EFFECT.PunishAttack;

/**
 * A skill's PUNISH clause — bonus payout when a condition is already on the board.
 *
 * This is the second half of a skill, kept separate from `special` on purpose. `special`
 * APPLIES a condition and is what {@link COMBAT_ARCHETYPE} identity is validated against
 * (see `combatArchetypes.ts`); `punish` CONVERTS one and is free to differ. A species can
 * therefore apply a bleed and punish a bleed with the same magic without the archetype
 * invariant or the Battle Cards value derivation ever seeing a changed `special`.
 *
 * Each effect carries its own verb, resolved in `battleEngine.ts`:
 * - `exploit_dot`    — DETONATE: deal the target's remaining bleed ticks at once, then clear it
 * - `exploit_stun`   — PUNISH: bonus damage while the target is under any control effect
 * - `exploit_wound`  — STEAL: bonus damage against a wounded target, and heal that much
 * - `exploit_shield` — CASH OUT: bonus damage equal to your own held shield, which is spent
 * - `punish_attack`  — bonus damage when the target's last commit was attack
 */
export type CombatPunish = {
  effect: CombatPunishEffect;
  /**
   * Bonus damage when the condition is met. For `exploit_shield` this is a CAP: the payout
   * is the shield actually held, never more than this.
   */
  value: number;
};

/**
 * Individual combat skill definition
 */
export type CombatSkill = {
  /** Display name for the skill */
  name: string;
  /** Base damage dealt (0 for non-damaging skills) */
  damage: number;
  /** HP healed (0 for non-healing skills) */
  heal: number;
  /** Special effect tag for game logic. Use COMBAT_SPECIAL_EFFECT.None for plain skills. */
  special: CombatSpecialEffect;
  /**
   * Parameter for special effects (optional)
   * - For "dot": bleed damage dealt next turn
   * - For "drainlife": HP healed next turn
   * - For "execute": damage dealt when opponent < 50% HP
   * - For "shield": shield HP that absorbs the next incoming damage
   * - For "stun": optional boosted stun chance when the target was bleeding this turn
   * - For "heal": HP healed immediately when attack hits
   * - For "exploit_dot"/"exploit_wound"/"exploit_stun"/"punish_attack": bonus damage when condition met
   * - Not used for other effects
   */
  specialValue?: number;
  /** Chance for special effect to trigger (0-1, optional) */
  specialChance?: number;
  /**
   * Optional PUNISH clause — what this skill pays when a condition is already on the
   * target (or, for `exploit_shield`, on the caster). Independent of `special`, which is
   * what the skill APPLIES. See {@link CombatPunish}.
   */
  punish?: CombatPunish;
  /** Emoji to display in UI */
  emoji: string;
  /** Human-readable description of what this skill does */
  description: string;
};

/**
 * Per-species combat tuning values (meta balance knobs).
 * All fields optional - defaults from DEFAULT_COMBAT_TUNING in battleConstants.ts.
 */
export type CombatTuning = {
  /** Max identical actions allowed before action lock triggers. Default: 4 */
  actionLockStreak?: number;
  /** Success rate for force_action effects (force_magic, force_recover, etc.). Default: 0.95 */
  forceActionChance?: number;
  /** Success rate for block_action effects (block_magic, block_recover, etc.). Default: 0.95 */
  blockActionChance?: number;
};

/**
 * PVP combat configuration for a species
 */
export type SpeciesCombatConfig = {
  /** Strategy archetype (cluster). Must match the magic special — see combatArchetypes.ts. */
  archetype: CombatArchetype;
  /** Base HP for PVP combat (actual HP = baseHp + currentStage) */
  baseHp: number;
  /** Attack skill (physical damage) */
  attack: CombatSkill;
  /** Recover skill (heal) */
  recover: CombatSkill;
  /** Magic skill (unique per species) */
  magic: CombatSkill;
  /** Dodge skill (evasion) */
  dodge: CombatSkill;
  /** Number of Recover uses allowed per stage index (e.g., [0, 2, 3] = 0 at stage 0, 2 at stage 1, 3 at stage 2+) */
  recoverUsesByStage: number[];
  /** Per-species combat tuning knobs */
  tuning?: CombatTuning;
  /** If true, this species' attacks cannot be dodged (precise attack mechanic) */
  preciseAttack?: boolean;
};

/**
 * Complete species definition.
 * This is the data structure synced to the species_configs database table.
 */
export type SuikamonSpecies = {
  /** Unique species identifier (0-N) */
  id: number;
  /** Display name of the species */
  name: string;
  /** Element type for categorization and theming */
  element: ElementId;
  /** Rarity tier (affects pricing, supply caps, visual styling) */
  rarity: Rarity;
  /** Short tagline shown in species selection */
  tagline: string;
  /** Full description with lore and personality */
  description: string;
  /** Emoji used as avatar in UI */
  avatarEmoji: string;
  /** Whether this species is available for adoption */
  available: boolean;
  /** XP progression and evolution configuration */
  behavior: SpeciesBehavior;
  /** Adoption price in SUI tokens */
  adoptionPrice: number;
  /** Percentage of total supply reserved for breeding (0-100). Last X% eggs are breeding-only. Defaults to 0 (all adoptable). */
  breedingReservedPercentage?: number;
  /** Price multiplier discount (1 = 100% price, no discount; <1 = on sale). Defaults to 1. */
  discount?: number;
  /** PVP combat configuration (optional - species without combat config can't PVP) */
  combat?: SpeciesCombatConfig;
};

// ============================================================================
// Species UI Constants
// ============================================================================

/**
 * Element emoji mapping for UI display.
 */
export const ELEMENT_EMOJI: Record<ElementId, string> = {
  Nature: "🌱",
  Water: "💧",
  Thunder: "⚡",
  Fire: "🔥",
  ComingSoon: "⏳",
};

/**
 * Skill type emoji mapping for consistent UI display across all components.
 * Used in combat UI, skill showcases, and PVP lab.
 */
export const SKILL_TYPE_EMOJI = {
  Nature: "🌿",
  Water: "💧", 
  Fire: "🔥",
  Thunder: "⚡",
  Attack: "🗡️",
  Recover: "❤️",
  Magic: "✨" // Fallback for magic when element not specified
} as const;

export type SkillTypeKey = keyof typeof SKILL_TYPE_EMOJI;

/**
 * The painted face for the five real ELEMENTS in the map above.
 *
 * `Attack`, `Recover` and `Magic` are deliberately absent: they are GESTURES, not elements, and
 * they were painted with `COMBAT_ACTION_ART`. They only sit in `SKILL_TYPE_EMOJI` because one
 * lookup grew to serve two vocabularies — a fact worth un-picking, but not by giving a gesture
 * a second drawing here.
 *
 * Subjects and palettes live in `shared/domain/uiIconArt.ts`; this is the lookup the species
 * chips and the type-matchup tables use. The emoji map stays as the text form.
 */
export const SKILL_TYPE_ART: Partial<Record<SkillTypeKey, string>> = {
  Nature: "/img/element-nature.png",
  Water: "/img/element-water.png",
  Fire: "/img/element-fire.png",
  Thunder: "/img/element-thunder.png",
};

/**
 * Rarity color mapping for UI styling.
 * Colors follow standard game rarity conventions.
 */
export const RARITY_COLORS: Record<Rarity, string> = {
  [Rarity.Common]: '#00e660ff',
  [Rarity.Rare]: '#0070dd',
  [Rarity.Epic]: '#a335ee',
  [Rarity.Legendary]: '#ff8000',
  [Rarity.Unique]: '#e6cc80',
};

// ============================================================================
// Rarity Utilities
// ============================================================================

/**
 * Convert Rarity enum to blockchain numeric representation.
 * Returns the rarity as a number for on-chain contracts.
 *
 * @param rarity - Rarity enum value or undefined
 * @returns Numeric rarity (0=Common, 1=Rare, 2=Epic, 3=Legendary, 4=Unique)
 */
export function rarityToNumber(rarity: Rarity | undefined): number {
  switch (rarity) {
    case "Common": return 0;
    case "Rare": return 1;
    case "Epic": return 2;
    case "Legendary": return 3;
    case "Unique": return 4;
    default: return 0;
  }
}
